<script setup>
import { onMounted, ref, watch, provide, computed } from "vue";
import moment from "moment";
import { useStore } from "vuex";
import { Input } from "@/components/v4/form";
import debounce from "lodash/debounce";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/v4/tabs";
import { IconSearch, IconXboxXFilled } from "@tabler/icons-vue";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/v4/popover";
import { Separator } from "@/components/v4/separator";
import { useQuery } from "@tanstack/vue-query";
import { httpClientOmni } from "@/libraries";

import SearchDisplay from "./SearchDisplay";

const store = useStore();

const isOpenSearchPopover = ref(false);
const searchInput = ref(null);
const selectedTab = ref("");
const disableTab = ref(true);
const searchValue = ref("");
const searchedProject = ref([]);

const userApp = computed(() => store.state.app.userApp);
const user = computed(() => store.getters["localStorage"]?.user);

const setProject = () => {
  const appList = userApp.value.filter((app) => {
    return app.name.toLowerCase().match(searchValue.value.toLowerCase());
  });

  const selectedApp = searchValue.value ? appList : [];

  const transformedList = selectedApp.map((app) => {
    return {
      name: app.name,
      iconColor: "var(--neutral-400)",
      subdesc: [app.description],
      hashed_id: app.hashed_id,
    };
  });

  searchedProject.value = transformedList.slice(0, 5);
};

const tickets = computed(() => {
  // const tix = store.getters["app/tickets"];
  const tix = querySearch.data.value?.data?.tickets || [];
  const transformedList = tix.map((tx) => {
    return {
      id: tx.id,
      name: tx.subject,
      iconColor: "var(--warning-500)",
      subdesc: [moment(tx.created_at).format("dddd, DD MMMM YYYY")],
    };
  });
  return transformedList.slice(0, 5);
});

const customers = computed(() => {
  const cust = querySearch.data.value?.data.customers || [];
  const transformedList = cust.map((cs) => {
    const sdc = cs.phone ? [cs.phone, " • ", cs.email] : [cs.email];
    return {
      name: cs.name,
      iconColor: "var(--success-500)",
      subdesc: sdc,
      hashed_id: cs.hashed_id,
    };
  });
  return transformedList.slice(0, 5);
});

let timeout;

const onInputSearch = (e) => {
  if (e.target.value.length > 0) {
    isOpenSearchPopover.value = true;

    clearTimeout(timeout);

    setTimeout(() => {
      disableTab.value = false;
    }, 100);

    timeout = setTimeout(() => {
      if (!selectedTab.value) {
        selectedTab.value = "all";
      }
    }, 200);
  } else {
    clearState();
    clearTimeout(timeout);
  }
};

const clearState = () => {
  isOpenSearchPopover.value = false;
  disableTab.value = true;
  selectedTab.value = "";
  searchValue.value = "";
  setTimeout(() => {
    searchInput.value.inputRef.focus();
  }, 200);
};

const querySearch = useQuery({
  queryKey: ["SEARCH", searchValue.value],
  queryFn: async () => {
    const { data: response } = await httpClientOmni.post(`v3/search`, {
      filterText: searchValue.value,
    });
    return response;
  },
  enabled: false,
});

const debouncedSearch = debounce(async () => {
  setProject();
  await querySearch.refetch();
}, 500);

watch(
  () => searchValue.value,
  () => {
    debouncedSearch();
  }
);

onMounted(async () => {
  await store.dispatch("app/getUserApp", user.value.id);
});

provide(
  "isLoading",
  computed(() => querySearch.isLoading.value || querySearch.isFetching.value)
);

provide(
  "searchValue",
  computed(() => searchValue.value)
);
</script>

<template>
  <Popover :open="isOpenSearchPopover">
    <PopoverTrigger>
      <Input
        placeholder="Search here..."
        class="!vf-rounded-[50px] vf-w-[200px] vf-bg-neutral-100"
        @input="onInputSearch"
        ref="searchInput"
        v-model="searchValue"
        :onClickSuffix="clearState"
      >
        <template #prefix>
          <IconSearch color="var(--neutral-400)" size="20" />
        </template>
        <template #suffix v-if="searchValue.length">
          <IconXboxXFilled color="var(--neutral-400)" size="20" />
        </template>
      </Input>
    </PopoverTrigger>
    <PopoverContent class="vf-w-[500px] !vf-rounded-lg">
      <Tabs
        :model-value="selectedTab"
        @update:modelValue="selectedTab = $event"
      >
        <TabsList class="grid w-full grid-cols-2 !vf-justify-start">
          <TabsTrigger :disabled="disableTab" value="all"> All </TabsTrigger>
          <TabsTrigger :disabled="disableTab" value="project">
            Project
          </TabsTrigger>
          <TabsTrigger :disabled="disableTab" value="tickets">
            Tickets
          </TabsTrigger>
          <TabsTrigger :disabled="disableTab" value="customers">
            Customers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all" class="vf-max-h-[500px] vf-overflow-auto">
          <SearchDisplay title="Project" :data="searchedProject" />
          <Separator class="vf-my-4" />
          <SearchDisplay title="Tickets" :data="tickets" />
          <Separator class="vf-my-4" />
          <SearchDisplay title="Customer" :data="customers" />
        </TabsContent>
        <TabsContent value="project" class="vf-max-h-[500px] vf-overflow-auto">
          <SearchDisplay title="Project" :data="searchedProject" />
        </TabsContent>
        <TabsContent value="tickets" class="vf-max-h-[500px] vf-overflow-auto">
          <SearchDisplay title="Tickets" :data="tickets" />
        </TabsContent>
        <TabsContent
          value="customers"
          class="vf-max-h-[500px] vf-overflow-auto"
        >
          <SearchDisplay title="Customer" :data="customers" />
        </TabsContent>
      </Tabs>
    </PopoverContent>
  </Popover>
</template>
